import React from 'react'
import moment from 'moment'
import Helmet from 'react-helmet'
import startCase from 'lodash/startCase'
import siteConfig from '../../siteConfig'
import {
  person,
  website,
  organization,
  blog,
  socialProfile,
  article,
  review,
  breadCrumbList,
} from 'utils/meta'

const myName = 'Christopher Tran'
const name = 'healthfulinspired'
const pinterestUrl = process.env.PINTEREST_URL
const twitterUrl = process.env.TWITTER_URL
const instagramUrl = process.env.INSTAGRAM_URL
const tumblrUrl = process.env.TUMBLR_URL
const googlePlusUrl = process.env.GOOGLEPLUS_URL
const facebookPage = process.env.FACEBOOK_PAGE
const linkedinUrl = process.env.LINKEDIN_URL
const facebookUrl = process.env.FACEBOOK_URL

/* -------------------------------------------------------
  ---- ARTICLE METADATA
-------------------------------------------------------- */

export function ArticleMeta({
  title,
  shortDescription,
  thumbnail,
  genre,
  tags,
  wordCount,
  datePublished,
  dateModified,
  articleBody,
  path,
}) {
  const url = siteConfig.siteUrl + path
  const structuredData = [
    article({
      headline: title,
      alternativeHeadline: shortDescription,
      image: thumbnail,
      author: name,
      editor: name,
      genre: genre || 'health', // genre === categories[0]
      keywords: tags,
      wordcount: wordCount,
      publisher: siteConfig.siteName,
      url,
      datePublished,
      dateCreated: datePublished,
      dateModified,
      shortDescription,
      articleBody,
      mainEntityOfPage: url,
    }),
    socialProfile({
      name,
      url,
      socialUrls: [
        pinterestUrl,
        twitterUrl,
        instagramUrl,
        tumblrUrl,
        googlePlusUrl,
        facebookPage,
      ],
    }),
    breadCrumbList({
      sites: [
        { url: siteConfig.siteUrl + '/', name: 'Home' },
        {
          url: siteConfig.siteUrl + '/' + genre,
          name: startCase(genre),
        },
      ],
    }),
  ]

  return (
    <Helmet
      title={title}
      meta={[
        { name: 'description', content: shortDescription },
        { name: 'image', content: thumbnail },
        { name: 'keywords', content: tags },
        { name: 'og:type', content: 'article' },
        { name: 'og:title', content: title },
        { name: 'og:description', content: shortDescription },
        { name: 'og:url', content: url },
        { name: 'og:site_name', content: siteConfig.siteName },
        { name: 'og:image', content: thumbnail },
        { name: 'og:updated_time', content: dateModified },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: title },
        { name: 'twitter:description', content: shortDescription },
        { name: 'twitter:image', content: thumbnail },
        { name: 'twitter:creator', content: '@healthfulinspired' },
        { property: 'article:published_time', content: datePublished },
        { property: 'article:modified_time', content: dateModified },
        { property: 'article:author', content: pinterestUrl },
        { property: 'article:publisher', content: pinterestUrl },
        { property: 'article:section', content: genre },
        { property: 'article:tag', content: tags },
      ]}
      script={[
        {
          type: 'application/ld+json',
          innerHTML: JSON.stringify(structuredData),
        },
        {
          innerHTML: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','GTM-PSCNFN5');`,
        },
        // {
        //   // Red Tea Detox ebook popup form
        //   type: 'text/javascript',
        //   src:
        //     'https://app.getresponse.com/view_webform_v2.js?u=hu1xu&webforms_id=BYtLa',
        // },
        // {
        //   // Scroll subscribe form
        //   type: 'text/javascript',
        //   src:
        //     'https://app.getresponse.com/view_webform_v2.js?u=hu1xu&webforms_id=24515904',
        // },
      ]}
    >
      <link rel="canonical" href={url} />
    </Helmet>
  )
}

/* -------------------------------------------------------
  ---- ARTICLE METADATA
-------------------------------------------------------- */

export function ReviewMeta({
  productImage,
  reviewTitle,
  reviewRatingValue,
  productName,
  reviewAuthor,
  reviewPublisher,
  datePublished,
  dateModified,
  reviewBody,
  path,
  genre,
  shortDescription,
  tags,
}) {
  const url = siteConfig.siteUrl + path
  const structuredData = [
    review({
      productImage,
      reviewTitle,
      reviewRatingValue,
      productName,
      reviewAuthor,
      reviewPublisher,
      datePublished,
      reviewBody,
    }),
    socialProfile({
      name,
      url,
      socialUrls: [
        pinterestUrl,
        twitterUrl,
        instagramUrl,
        tumblrUrl,
        googlePlusUrl,
        facebookPage,
      ],
    }),
    breadCrumbList({
      sites: [
        { url: siteConfig.siteUrl + '/', name: 'Home' },
        {
          url: siteConfig.siteUrl + '/' + genre,
          name: startCase(genre),
        },
      ],
    }),
  ]

  return (
    <Helmet
      title={reviewTitle}
      meta={[
        { name: 'description', content: shortDescription },
        { name: 'image', content: productImage },
        { name: 'keywords', content: tags },
        { name: 'og:type', content: 'article' },
        { name: 'og:title', content: reviewTitle },
        { name: 'og:description', content: shortDescription },
        { name: 'og:url', content: url },
        { name: 'og:site_name', content: siteConfig.siteName },
        { name: 'og:image', content: productImage },
        { name: 'og:updated_time', content: dateModified },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: reviewTitle },
        { name: 'twitter:description', content: shortDescription },
        { name: 'twitter:image', content: productImage },
        { property: 'article:published_time', content: datePublished },
        { property: 'article:modified_time', content: dateModified },
        { property: 'article:author', content: pinterestUrl },
        { property: 'article:publisher', content: pinterestUrl },
        { property: 'article:section', content: genre },
        { property: 'article:tag', content: tags },
      ]}
      script={[
        {
          type: 'application/ld+json',
          innerHTML: JSON.stringify(structuredData),
        },
        {
          innerHTML: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','GTM-PSCNFN5');`,
        },
        // {
        //   // Exit pop-up subscribe form
        //   type: 'text/javascript',
        //   src:
        //     'https://app.getresponse.com/view_webform_v2.js?u=hu1xu&webforms_id=24506304',
        // },
        // {
        //   // Red Tea Detox ebook popup form
        //   type: 'text/javascript',
        //   src:
        //     'https://app.getresponse.com/view_webform_v2.js?u=hu1xu&webforms_id=BYtLa',
        // },
      ]}
    >
      <link rel="canonical" href={url} />
    </Helmet>
  )
}

/* -------------------------------------------------------
  ---- BLOG POST META
------------------------------------------------------- */

export function BlogMeta({
  title,
  shortDescription,
  thumbnail,
  genre,
  tags,
  wordCount,
  datePublished,
  dateModified,
  articleBody,
  excerpt,
  path,
}) {
  const url = siteConfig.siteUrl + path

  const structuredData = [
    blog({
      headline: title,
      alternativeHeadline: shortDescription,
      image: thumbnail,
      editor: name,
      genre: genre || 'health', // genre === categories[0]
      keywords: tags,
      wordcount: wordCount,
      publisher: siteConfig.siteName,
      url,
      datePublished,
      dateCreated: datePublished,
      dateModified,
      description: shortDescription,
      articleBody,
      author: name,
      mainEntityOfPage: url,
    }),
    socialProfile({
      name,
      url,
      socialUrls: [
        pinterestUrl,
        twitterUrl,
        instagramUrl,
        tumblrUrl,
        googlePlusUrl,
        facebookPage,
      ],
    }),
    breadCrumbList({
      sites: [
        { url: siteConfig.siteUrl + '/', name: 'Home' },
        {
          url: siteConfig.siteUrl + '/' + genre,
          name: startCase(genre),
        },
      ],
    }),
  ]

  return (
    <Helmet
      title={title}
      meta={[
        { name: 'description', content: shortDescription },
        { name: 'image', content: thumbnail },
        { name: 'keywords', content: tags },
        { name: 'og:type', content: 'article' },
        { name: 'og:title', content: title },
        { name: 'og:description', content: shortDescription },
        { name: 'og:url', content: url },
        { name: 'og:site_name', content: siteConfig.siteName },
        { name: 'og:image', content: thumbnail },
        { name: 'og:image:width', content: '1667' },
        { name: 'og:image:height', content: '2500' },
        { name: 'og:updated_time', content: dateModified },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: title },
        { name: 'twitter:description', content: shortDescription },
        { name: 'twitter:image', content: thumbnail },
        { property: 'article:published_time', content: datePublished },
        { property: 'article:modified_time', content: dateModified },
        { property: 'article:author', content: pinterestUrl },
        { property: 'article:publisher', content: pinterestUrl },
        { property: 'article:section', content: 'health' },
        { property: 'article:tag', content: tags },
      ]}
      script={[
        {
          type: 'application/ld+json',
          innerHTML: JSON.stringify(structuredData),
        },
        {
          innerHTML: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','GTM-PSCNFN5');`,
        },
      ]}
    >
      <link rel="canonical" href={url} />
    </Helmet>
  )
}

/* -------------------------------------------------------
  ---- AD METADATA
-------------------------------------------------------- */

export function AdMeta({
  title,
  shortDescription,
  excerpt,
  tags,
  path,
  thumbnail,
  datePublished,
  dateModified,
}) {
  const url = siteConfig.siteUrl + path
  const structuredData = [
    article({
      id: url,
      name: title,
      image: thumbnail,
      headline: title,
      shortDescription,
      datePublished,
      dateModified,
      author: myName,
      publisher: { name: myName, logo: siteConfig.remoteLogo },
      section: 'Health',
      body: excerpt,
      mainEntityOfPage: url,
    }),
    socialProfile({
      name: myName,
      url,
      socialUrls: [linkedinUrl, facebookUrl],
    }),
  ]

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={shortDescription} />
      <meta name="image" content={thumbnail} />
      <meta name="keywords" content={tags} />
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
      <meta property="og:type" content="article" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={shortDescription} />
      <meta property="og:url" content={url} />
      <meta property="og:site_name" content={siteConfig.name} />
      <meta property="og:image" content={thumbnail} />
      <meta property="article:published_time" content={datePublished} />
      <meta property="article:modified_time" content={dateModified} />
      <meta property="article:author" content={myName} />
      <meta property="article:section" content="health" />
      <meta property="article:tag" content={tags} />
      <link rel="canonical" href={url} />
    </Helmet>
  )
}

/* -------------------------------------------------------
  ---- ROOT META DATA
-------------------------------------------------------- */

export function RootMeta({ url, title, description, breadCrumb = [] }) {
  const structuredData = [
    website({
      url: url || siteConfig.siteUrl,
      name: siteConfig.siteName,
    }),
    organization({
      url: url || siteConfig.siteUrl,
      name: siteConfig.siteName,
      logo: siteConfig.remoteLogo,
      foundingDate: moment('2010-04-01').toISOString(),
      founders: ['Elisa Roberts', 'Christopher Tran'],
      sameAs: [
        pinterestUrl,
        twitterUrl,
        instagramUrl,
        tumblrUrl,
        googlePlusUrl,
        facebookPage,
      ],
    }),
    socialProfile({
      name,
      url,
      socialUrls: [
        pinterestUrl,
        twitterUrl,
        instagramUrl,
        tumblrUrl,
        googlePlusUrl,
        facebookPage,
      ],
    }),
    breadCrumbList({
      sites: [{ url: siteConfig.siteUrl + '/', name: 'Home' }, ...breadCrumb],
    }),
  ]
  return (
    <Helmet
      title={title}
      meta={[
        { name: 'description', content: description },
        { property: 'og:locale', content: 'en_US' },
        { property: 'og:type', content: 'website' },
        { property: 'og:title', content: siteConfig.siteTitle },
        { property: 'og:description', content: siteConfig.siteDescription },
        { property: 'og:url', content: siteConfig.siteUrl },
        { property: 'og:site_name', content: siteConfig.siteName },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:description', content: siteConfig.siteDescription },
        { name: 'twitter:title', content: siteConfig.siteTitle },
      ]}
      script={[
        {
          type: 'application/ld+json',
          innerHTML: JSON.stringify(structuredData),
        },
        {
          innerHTML: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','GTM-PSCNFN5');`,
        },
      ]}
    >
      <link rel="canonical" href={url} />
    </Helmet>
  )
}



// WEBPACK FOOTER //
// ./src/components/Meta.js